/**
 * AffirmationSelection — Preview the affirmations before immersion
 * 
 * Shows the 5 healing categories and their affirmations so users
 * can see exactly what will be immersed into their mind.
 * Read-only preview — all categories are included.
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  // Post-op spinal recovery
  post_op_healing:   { label: 'Post-Op Healing',    description: 'Tissue knitting closed, incisions sealing' },
  nerve_calming:     { label: 'Nerve Calming',      description: 'The nerve is safe, insulated, soothed' },
  spinal_space:      { label: 'Creating Space',     description: 'Vertebrae floating apart, discs plumping' },
  pain_release:      { label: 'Pain Release',       description: 'Signals softening, comfort returning' },
  deep_recovery:     { label: 'Deep Recovery',      description: 'Full-body integration and rest' },
  // Legacy Spinal Ease categories (kept so other conditions still render)
  safety:            { label: 'Safety',             description: 'Feeling safe and grounded' },
  releasing:         { label: 'Releasing',          description: 'Letting go of held tension' },
  spinal_softening:  { label: 'Spinal Softening',   description: 'Ease returning to your spine' },
  openness:          { label: 'Openness',           description: 'Trusting and opening' },
  reconditioning:    { label: 'Reconditioning',     description: 'Rewiring toward ease' },
};

const CATEGORY_ORDER = [
  'post_op_healing', 'nerve_calming', 'spinal_space', 'pain_release', 'deep_recovery',
  'safety', 'releasing', 'spinal_softening', 'openness', 'reconditioning',
];

const AffirmationSelection = ({ condition = 'spinal-ease', onContinue }) => {
  const [categories, setCategories] = useState({});
  const [expandedCat, setExpandedCat] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAffirmations = async () => {
      try {
        const res = await fetch(`${API}/api/healing/affirmations/${condition}`);
        const data = await res.json();
        if (data.categories) setCategories(data.categories);
      } catch (e) {
        console.error('Failed to load affirmations:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAffirmations();
  }, [condition]);

  const toggleExpand = (cat) => {
    setExpandedCat(prev => prev === cat ? null : cat);
  };

  const handleContinue = () => {
    const allAffirmations = [];
    for (const cat of CATEGORY_ORDER) {
      if (categories[cat]) allAffirmations.push(...categories[cat]);
    }
    onContinue(allAffirmations);
  };

  const orderedCategories = CATEGORY_ORDER.filter(c => categories[c]);
  const totalCount = orderedCategories.reduce((sum, c) => sum + (categories[c]?.length || 0), 0);

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center overflow-y-auto"
      style={{ background: 'linear-gradient(180deg, #080b08 0%, #0a0e0a 50%, #080b08 100%)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 2, ease: 'easeInOut' }}
      data-testid="affirmation-selection"
    >
      <div className="w-full max-w-lg px-6 py-12 flex flex-col items-center min-h-screen">
        {/* Header */}
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1.2 }}
        >
          <p className="text-white/20 text-xs tracking-[0.25em] uppercase mb-6"
            style={{ fontFamily: "'Manrope', sans-serif" }}>
            Your Immersion
          </p>
          <h2 className="text-white/55 text-xl mb-3"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
            What will enter your mind
          </h2>
          <p className="text-white/15 text-sm max-w-xs mx-auto"
            style={{ fontFamily: "'Manrope', sans-serif" }}>
            {totalCount} affirmations across {orderedCategories.length} areas
          </p>
        </motion.div>

        {/* Categories — read only, tap to expand */}
        {loading ? (
          <motion.p
            className="text-white/15 text-sm"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            animate={{ opacity: [0.1, 0.2, 0.1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            Loading...
          </motion.p>
        ) : (
          <div className="w-full space-y-3 mb-12">
            {orderedCategories.map((cat, i) => {
              const isExpanded = expandedCat === cat;
              const meta = CATEGORY_META[cat] || { label: cat, description: '' };
              const affirmations = categories[cat] || [];

              return (
                <motion.div
                  key={cat}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + i * 0.12, duration: 0.8 }}
                >
                  {/* Category header — tap to reveal affirmations */}
                  <motion.div
                    className="flex items-center gap-3 w-full px-5 py-4 rounded-2xl cursor-pointer"
                    style={{
                      background: isExpanded ? 'rgba(140,170,120,0.06)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${isExpanded ? 'rgba(140,170,120,0.12)' : 'rgba(255,255,255,0.04)'}`,
                      transition: 'all 0.5s ease',
                    }}
                    onClick={() => toggleExpand(cat)}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`affirmation-cat-${cat}`}
                  >
                    {/* Soft dot */}
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: 'rgba(140,170,120,0.25)' }}
                    />

                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white/40"
                        style={{ fontFamily: "'Manrope', sans-serif" }}>
                        {meta.label}
                      </p>
                      <p className="text-xs text-white/15 mt-0.5"
                        style={{ fontFamily: "'Manrope', sans-serif" }}>
                        {meta.description}
                      </p>
                    </div>

                    <span className="text-white/12 text-xs"
                      style={{ fontFamily: "'Manrope', sans-serif" }}>
                      {affirmations.length}
                    </span>
                  </motion.div>

                  {/* Expanded affirmations list */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        className="px-5 pt-2 pb-1"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4 }}
                        data-testid={`affirmation-list-${cat}`}
                      >
                        {affirmations.map((text, j) => (
                          <motion.p
                            key={j}
                            className="text-white/20 text-xs py-1.5 border-b border-white/[0.03] last:border-0"
                            style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: j * 0.05 }}
                          >
                            {text}
                          </motion.p>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Continue */}
        <motion.button
          className="w-64 py-3.5 rounded-full text-sm"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontStyle: 'italic',
            background: 'rgba(140,170,120,0.1)',
            border: '1px solid rgba(140,170,120,0.12)',
            color: 'rgba(255,255,255,0.45)',
          }}
          onClick={handleContinue}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          data-testid="affirmation-continue-btn"
        >
          Begin immersion
        </motion.button>

        <div className="mt-auto pt-8 pb-4">
          <p className="text-white/6 text-[9px] text-center" style={{ fontFamily: "'Manrope', sans-serif" }}>
            These affirmations appear as gentle subliminal impressions during your experience.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default AffirmationSelection;
